import { mapValues, uniqBy } from 'lodash';
import {
  AlignmentItem,
  allSdgAlignmentLevels,
  allSdgIds,
  ByAlignmentLevel,
  BySdgAndAlignmentLevel,
  SdgAlignment,
  SdgAlignmentByLevel,
  SdgAlignmentLevel,
  SdgAlignmentsBySdgAndLevel,
  SdgAlignmentsWithWeightAndId,
  SdgAlignmentValence,
  SdgId,
} from './types';

export const sdgSheetId = '1dNjU0IGWz4oJE0r7vAOL0upqNE-wRhMxpP2MjwqHl34';

// Used by the platform SDG lens (sdg-alignment-bar), the chat MCP backend
// (`get_sdg_alignment`), and the chat MCP card (`mcp-apps/sdg-alignment`).
// Keep them in sync: any new path that surfaces SDG alignment values to a user
// should hide values below this threshold so the chat can never quote a number
// the platform UI hides. PLT-4604.
export const sdgShowThreshold = 0.001;

export function formatSdgAlignmentAsPercentage(
  alignment: number | undefined,
  decimals: number = 1,
  tight?: boolean,
): string {
  if (alignment === undefined) {
    return 'N/A';
  }
  function format(value: number) {
    return `${(value * 100).toFixed(decimals)}${tight ? '%' : ' %'}`;
  }

  if (alignment > 0 && alignment < sdgShowThreshold) {
    return `< ${format(sdgShowThreshold)}`;
  }
  return `${format(alignment)}`;
}

export function getTopContributors(
  items: AlignmentItem[],
  topContributorCount: number = DEFAULT_CONTRIBUTOR_COUNT,
): AlignmentItem[] {
  return uniqBy(
    items.sort((a, b) => getWeightedAlignment(b) - getWeightedAlignment(a)),
    d => d.id,
  ).slice(0, topContributorCount);
}

function getWeightedAlignment(item: AlignmentItem) {
  return item.alignment * (item.weight || 1);
}

export const DEFAULT_CONTRIBUTOR_COUNT = 1;

export function mapSdgAlignmentLevelDatums<T = SdgAlignment>(
  alignments: SdgAlignmentsBySdgAndLevel,
  apply: (alignment: SdgAlignment | undefined, sdg?: SdgId, level?: SdgAlignmentLevel) => T | undefined,
): BySdgAndAlignmentLevel<T> {
  return mapValues(alignments, (alignmentsOfSdg: ByAlignmentLevel<SdgAlignment>, sdg: SdgId) =>
    mapValues(alignmentsOfSdg, (alignmentOfLevel: SdgAlignment, level: SdgAlignmentLevel) =>
      apply(alignmentOfLevel, sdg, level),
    ),
  );
}
/**
 * A helper to go through SDG alignments and combine them using a provided function
 * @param combinator A function that handles the combination and returns one SdgAlignmentWithItems.
 * sdgId and alignmentLevel params are optional and can be used for customization per sdg or debug logging
 * @returns combined sdg alignments.
 */
export function combineSdgAlignments(
  alignments1: SdgAlignmentsWithWeightAndId,
  alignments2: SdgAlignmentsWithWeightAndId,
  combinator: (
    input1: SdgAlignmentsWithWeightAndId,
    input2: SdgAlignmentsWithWeightAndId,
    sdgId: SdgId,
    alignmentLevel: SdgAlignmentLevel,
  ) => SdgAlignment | undefined,
) {
  const ret: SdgAlignmentsBySdgAndLevel | undefined = allSdgIds.reduce(
    (reducedForAllSdgs: SdgAlignmentsBySdgAndLevel | undefined, sdgId) => {
      const combinedForSdg = allSdgAlignmentLevels.reduce(
        (reducedForSdg: SdgAlignmentByLevel | undefined, alignmentLevel) => {
          const combinedForAlignmentLevel = combinator(alignments1, alignments2, sdgId, alignmentLevel);
          return addToCurrOrCreateIfUndefined(reducedForSdg, combinedForAlignmentLevel, alignmentLevel);
        },
        undefined,
      );
      return addToCurrOrCreateIfUndefined(reducedForAllSdgs, combinedForSdg, sdgId);
    },
    undefined,
  );
  return ret;
}

/**
 * A helper to either create or return a reduced object. Helpful when using a reducer with
 * undefined as the initial value.
 * @param acc The object reduced so far
 * @param curr The current value to add
 * @param key The key to add the current value to
 * @returns A new object of type Acc. Will create a new object if acc is undefined.
 * If both acc and curr are undefined, will return undefined.
 */
function addToCurrOrCreateIfUndefined<Key extends string, Curr, Acc extends { [k in Key]?: Curr | undefined }>(
  acc: Acc | undefined,
  curr: Curr | undefined,
  key: Key,
): Acc | undefined {
  if (!acc) {
    if (curr) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return { [key]: curr } as Acc;
    }
    return undefined;
  }
  if (curr) {
    const updated: Acc = { ...acc, [key]: curr };
    return updated;
  }
  return acc;
}

export function sdgAlignmentLevelToValence(level: SdgAlignmentLevel) {
  switch (level) {
    case SdgAlignmentLevel.STRONGLY_MISALIGNED:
    case SdgAlignmentLevel.MISALIGNED:
    case SdgAlignmentLevel.WEAKLY_MISALIGNED:
      return SdgAlignmentValence.MISALIGNED;
    case SdgAlignmentLevel.STRONGLY_ALIGNED:
    case SdgAlignmentLevel.ALIGNED:
    case SdgAlignmentLevel.WEAKLY_ALIGNED:
      return SdgAlignmentValence.ALIGNED;
    default:
      throw Error();
  }
}
